import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  createUser,
  deactivateUser,
  getUser,
  getUsersExportUrl,
  listUsers,
  reactivateUser,
  resetUserOtp,
  unlockUser,
  updateUser,
} from "../api/endpoints/users"
import {
  Button,
  DataTable,
  Drawer,
  EmptyState,
  Modal,
  PageSection,
  StatCard,
  StatusPill,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Th,
} from "../components/ui"
import { useAuth } from "../stores/auth"
import { formatDateTime, formatEnumLabel } from "../utils/formatting"

interface UserFormState {
  fullName: string
  phoneNumber: string
  email: string
  altPhone: string
  role: string
  teamName: string
  notes: string
}

const ROLE_PERMISSION_ROWS = [
  {
    role: "SUPER_ADMIN",
    canManageUsers: "All roles",
    canReviewKyc: "Yes",
    canManageTargets: "Full",
    canManageDisbursements: "Full",
    canViewAudit: "All logs",
    canManageConfig: "Yes",
  },
  {
    role: "ADMIN",
    canManageUsers: "Agent + Verification",
    canReviewKyc: "No",
    canManageTargets: "Full",
    canManageDisbursements: "No",
    canViewAudit: "No",
    canManageConfig: "No",
  },
  {
    role: "OPS_MANAGER",
    canManageUsers: "No",
    canReviewKyc: "No",
    canManageTargets: "Read only",
    canManageDisbursements: "No",
    canViewAudit: "No",
    canManageConfig: "No",
  },
  {
    role: "AGENT",
    canManageUsers: "No",
    canReviewKyc: "No",
    canManageTargets: "No",
    canManageDisbursements: "No",
    canViewAudit: "No",
    canManageConfig: "No",
  },
  {
    role: "VERIFICATION_STAFF",
    canManageUsers: "No",
    canReviewKyc: "Yes",
    canManageTargets: "No",
    canManageDisbursements: "No",
    canViewAudit: "No",
    canManageConfig: "No",
  },
] as const

const INITIAL_USER_FORM: UserFormState = {
  fullName: "",
  phoneNumber: "",
  email: "",
  altPhone: "",
  role: "AGENT",
  teamName: "",
  notes: "",
}

function statusTone(status: string) {
  if (status === "ACTIVE") return "success" as const
  if (status === "LOCKED" || status === "INACTIVE") return "warning" as const
  if (status === "DEACTIVATED") return "danger" as const
  return "neutral" as const
}

function initials(name: string) {
  return name
    .split(" ")
    .map((chunk) => chunk[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase()
}

function toUserPayload(form: UserFormState) {
  return {
    fullName: form.fullName,
    phoneNumber: form.phoneNumber,
    email: form.email.trim() || undefined,
    altPhone: form.altPhone.trim() || undefined,
    role: form.role,
    teamName: form.teamName.trim() || undefined,
    notes: form.notes.trim() || undefined,
  }
}

export function UsersPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(() => searchParams.get("search") ?? "")
  const [roleFilter, setRoleFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [teamFilter, setTeamFilter] = useState("")
  const [createdFrom, setCreatedFrom] = useState("")
  const [createdTo, setCreatedTo] = useState("")
  const [lastActiveFrom, setLastActiveFrom] = useState("")
  const [lastActiveTo, setLastActiveTo] = useState("")
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    () => searchParams.get("selected_user_id") ?? null,
  )
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [form, setForm] = useState<UserFormState>(INITIAL_USER_FORM)
  const [isEditing, setIsEditing] = useState(false)
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false)

  const managedRoles = user?.permissions?.managedRoles ?? ["AGENT"]
  const isSuperAdmin = user?.role === "SUPER_ADMIN"
  const filterRoles = useMemo(
    () => (isSuperAdmin ? ["SUPER_ADMIN", ...managedRoles] : managedRoles),
    [isSuperAdmin, managedRoles],
  )
  const formRoles = useMemo(
    () =>
      isSuperAdmin
        ? ["ADMIN", "OPS_MANAGER", "AGENT", "VERIFICATION_STAFF", "SUPER_ADMIN"]
        : managedRoles,
    [isSuperAdmin, managedRoles],
  )

  const queryParams = useMemo(
    () => ({
      ...(search ? { search } : {}),
      ...(roleFilter ? { role: roleFilter } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(teamFilter ? { team_name: teamFilter } : {}),
      ...(createdFrom ? { created_from: `${createdFrom}T00:00:00Z` } : {}),
      ...(createdTo ? { created_to: `${createdTo}T23:59:59Z` } : {}),
      ...(lastActiveFrom ? { last_active_from: `${lastActiveFrom}T00:00:00Z` } : {}),
      ...(lastActiveTo ? { last_active_to: `${lastActiveTo}T23:59:59Z` } : {}),
    }),
    [createdFrom, createdTo, lastActiveFrom, lastActiveTo, roleFilter, search, statusFilter, teamFilter],
  )

  const usersQuery = useQuery({
    queryKey: ["users", queryParams],
    queryFn: () => listUsers(queryParams),
  })

  const detailQuery = useQuery({
    queryKey: ["user-detail", selectedUserId],
    queryFn: () => getUser(selectedUserId!),
    enabled: Boolean(selectedUserId),
  })

  useEffect(() => {
    const routeSearch = searchParams.get("search") ?? ""
    if (routeSearch !== search) {
      setSearch(routeSearch)
    }

    const routeSelectedUserId = searchParams.get("selected_user_id") ?? null
    if (routeSelectedUserId !== selectedUserId) {
      setSelectedUserId(routeSelectedUserId)
    }
  }, [search, searchParams, selectedUserId])

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["users"] })
    if (selectedUserId) {
      await queryClient.invalidateQueries({ queryKey: ["user-detail", selectedUserId] })
    }
  }

  function updateRouteState(next: {
    search?: string | null
    selectedUserId?: string | null
  }) {
    const params = new URLSearchParams(searchParams)

    if (next.search !== undefined) {
      if (next.search) {
        params.set("search", next.search)
      } else {
        params.delete("search")
      }
    }

    if (next.selectedUserId !== undefined) {
      if (next.selectedUserId) {
        params.set("selected_user_id", next.selectedUserId)
      } else {
        params.delete("selected_user_id")
      }
    }

    setSearchParams(params)
  }

  const createMutation = useMutation({
    mutationFn: (payload: UserFormState) => createUser(toUserPayload(payload)),
    onSuccess: async () => {
      setForm(INITIAL_USER_FORM)
      await refresh()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (payload: UserFormState) => updateUser(selectedUserId!, toUserPayload(payload)),
    onSuccess: async () => {
      setIsEditing(false)
      setForm(INITIAL_USER_FORM)
      await refresh()
    },
  })

  const actionMutation = useMutation({
    mutationFn: async ({
      action,
      userId,
    }: {
      action: "unlock" | "resetOtp" | "deactivate" | "reactivate"
      userId: string
    }) => {
      const note = window.prompt("Enter an audit note for this action:", "Super admin account update") ?? ""
      if (!note) {
        return null
      }

      if (action === "unlock") return unlockUser(userId, note)
      if (action === "resetOtp") return resetUserOtp(userId, note)
      if (action === "deactivate") return deactivateUser(userId, note)
      return reactivateUser(userId, note)
    },
    onSuccess: async () => {
      await refresh()
    },
  })

  const bulkActionMutation = useMutation({
    mutationFn: async ({
      action,
      userIds,
    }: {
      action: "deactivate" | "reactivate"
      userIds: string[]
    }) => {
      const note =
        window.prompt("Enter an audit note for this bulk action:", "Bulk account update") ?? ""
      if (!note) {
        return null
      }

      if (action === "deactivate") {
        await Promise.all(userIds.map((userId) => deactivateUser(userId, note)))
        return
      }

      await Promise.all(userIds.map((userId) => reactivateUser(userId, note)))
    },
    onSuccess: async () => {
      setSelectedUserIds([])
      await refresh()
    },
  })

  const detail = detailQuery.data?.data
  const summary = usersQuery.data?.meta?.summary
  const rows = usersQuery.data?.data ?? []
  const roleBreakdown = [
    summary?.totalSuperAdmins ? `${summary.totalSuperAdmins} Super Admins` : null,
    summary?.totalAdmins ? `${summary.totalAdmins} Admins` : null,
    summary?.totalOpsManagers ? `${summary.totalOpsManagers} Ops Managers` : null,
    summary?.totalAgents ? `${summary.totalAgents} Agents` : null,
    summary?.totalVerificationStaff ? `${summary.totalVerificationStaff} Verification` : null,
  ]
    .filter(Boolean)
    .join(", ")

  function resetForm() {
    setForm(INITIAL_USER_FORM)
    setIsEditing(false)
  }

  function startEdit() {
    if (!detail) {
      return
    }

    setForm({
      fullName: detail.fullName,
      phoneNumber: detail.phoneNumber,
      email: detail.email ?? "",
      altPhone: detail.altPhone ?? "",
      role: detail.role,
      teamName: detail.teamName ?? "",
      notes: detail.notes ?? "",
    })
    setIsEditing(true)
  }

  function openUserDetail(userId: string) {
    setSelectedUserId(userId)
    updateRouteState({ selectedUserId: userId })
  }

  function toggleUserSelection(userId: string) {
    setSelectedUserIds((current) =>
      current.includes(userId) ? current.filter((entry) => entry !== userId) : [...current, userId],
    )
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (form.role === "SUPER_ADMIN") {
      const firstConfirm = window.confirm(
        "Creating or assigning the Super Admin role is a privileged action. Continue?",
      )
      const secondConfirm = firstConfirm
        ? window.confirm("Confirm that this user should have full platform access.")
        : false
      if (!secondConfirm) {
        return
      }
    }

    if (isEditing && selectedUserId) {
      if (detail?.role && detail.role !== form.role) {
        const confirmed = window.confirm(
          `Change role from ${formatEnumLabel(detail.role)} to ${formatEnumLabel(form.role)}?`,
        )
        if (!confirmed) {
          return
        }
      }

      updateMutation.mutate(form)
      return
    }

    createMutation.mutate(form)
  }

  return (
    <div className="flex flex-col gap-5">
      <PageSection title="Users & Roles">
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
            <input
              className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:w-72"
              onChange={(event) => {
                const nextSearch = event.target.value
                setSearch(nextSearch)
                updateRouteState({ search: nextSearch || null })
              }}
              placeholder="Search by name, email, phone, or public ID"
              value={search}
            />
            <select
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm sm:w-auto"
              onChange={(event) => setRoleFilter(event.target.value)}
              value={roleFilter}
            >
              <option value="">All roles</option>
              {filterRoles.map((role) => (
                <option key={role} value={role}>
                  {formatEnumLabel(role)}
                </option>
              ))}
            </select>
            <select
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm sm:w-auto"
              onChange={(event) => setStatusFilter(event.target.value)}
              value={statusFilter}
            >
              <option value="">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="LOCKED">Locked</option>
              <option value="DEACTIVATED">Deactivated</option>
            </select>
            <input
              className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:w-32"
              onChange={(event) => setTeamFilter(event.target.value)}
              placeholder="Team"
              value={teamFilter}
            />
            <input
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm sm:w-auto"
              onChange={(event) => setCreatedFrom(event.target.value)}
              type="date"
              value={createdFrom}
            />
            <input
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm sm:w-auto"
              onChange={(event) => setCreatedTo(event.target.value)}
              type="date"
              value={createdTo}
            />
            <input
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm sm:w-auto"
              onChange={(event) => setLastActiveFrom(event.target.value)}
              type="date"
              value={lastActiveFrom}
            />
            <input
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm sm:w-auto"
              onChange={(event) => setLastActiveTo(event.target.value)}
              type="date"
              value={lastActiveTo}
            />
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <a
              className="inline-flex w-full items-center justify-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 sm:w-auto"
              href={getUsersExportUrl(queryParams)}
            >
              Export CSV
            </a>
            <Button
              size="sm"
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={() => setIsPermissionsOpen(true)}
            >
              View Role Permissions
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={() =>
                bulkActionMutation.mutate({
                  action: "deactivate",
                  userIds: selectedUserIds,
                })
              }
              disabled={bulkActionMutation.isPending || selectedUserIds.length === 0}
            >
              Deactivate Selected
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() =>
                bulkActionMutation.mutate({
                  action: "reactivate",
                  userIds: selectedUserIds,
                })
              }
              disabled={bulkActionMutation.isPending || selectedUserIds.length === 0}
            >
              Reactivate Selected
            </Button>
          </div>
        </div>

        <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <StatCard label="Total Users" value={summary?.totalManagedUsers ?? 0} />
          <StatCard label="Active Users" value={summary?.totalActive ?? 0} />
          <StatCard label="Inactive Users" value={summary?.totalInactive ?? 0} />
          <StatCard label="Locked Accounts" value={summary?.totalLocked ?? 0} />
          <StatCard helper={roleBreakdown || "No users in scope"} label="By Role" value={roleBreakdown || "-"} />
        </div>

        {rows.length === 0 && !usersQuery.isLoading ? (
          <EmptyState
            description="Seed users locally or adjust the search filters."
            title="No managed users found"
          />
        ) : null}

        {rows.length > 0 ? (
          <div
            role="region"
            aria-label="Users table"
            className="max-w-full overflow-hidden rounded-xl border border-slate-200"
          >
            <DataTable>
              <TableHead>
                <Th className="w-12" />
                <Th className="min-w-[220px]">User</Th>
                <Th className="min-w-[140px]">Role</Th>
                <Th className="min-w-[220px]">Contact</Th>
                <Th className="min-w-[120px]">Team</Th>
                <Th className="min-w-[120px]">Status</Th>
                <Th className="whitespace-nowrap">Created</Th>
                <Th className="whitespace-nowrap">Last Seen</Th>
                <Th className="whitespace-nowrap">Failed Logins</Th>
              </TableHead>
              <TableBody>
                {rows.map((row: any) => (
                  <TableRow key={row.userId} onClick={() => openUserDetail(row.userId)}>
                    <Td className="w-12">
                      <div onClick={(event) => event.stopPropagation()}>
                        <input
                          checked={selectedUserIds.includes(row.userId)}
                          onChange={() => toggleUserSelection(row.userId)}
                          type="checkbox"
                        />
                      </div>
                    </Td>
                    <Td className="min-w-[220px]">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-700">
                          {initials(row.fullName)}
                        </div>
                        <div className="min-w-0">
                          <strong className="block truncate text-slate-900">{row.fullName}</strong>
                          <div className="truncate text-xs text-slate-500">{row.userId}</div>
                        </div>
                      </div>
                    </Td>
                    <Td className="min-w-[140px]">
                      <StatusPill tone={row.role === "SUPER_ADMIN" ? "danger" : "info"}>
                        {formatEnumLabel(row.role)}
                      </StatusPill>
                    </Td>
                    <Td className="min-w-[220px]">
                      <div className="min-w-0">
                        <div className="truncate">{row.phoneNumber}</div>
                        <div className="truncate text-xs text-slate-500">{row.email ?? "-"}</div>
                      </div>
                    </Td>
                    <Td className="min-w-[120px]">{row.teamName ?? "-"}</Td>
                    <Td className="min-w-[120px]">
                      <StatusPill tone={statusTone(row.accountStatus)}>{row.accountStatus}</StatusPill>
                    </Td>
                    <Td className="whitespace-nowrap">{formatDateTime(row.createdAt)}</Td>
                    <Td className="whitespace-nowrap">{formatDateTime(row.lastSeenAt)}</Td>
                    <Td className="whitespace-nowrap">
                      <span className={row.failedLoginAttempts > 0 ? "font-semibold text-red-600" : "text-slate-500"}>
                        {row.failedLoginAttempts ?? 0}
                      </span>
                    </Td>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </div>
        ) : null}
      </PageSection>

      <PageSection title={isEditing ? "Edit User" : "Create User"}>
        <form className="grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-slate-700">Full Name</span>
            <input
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              onChange={(event) => setForm((current) => ({ ...current, fullName: event.target.value }))}
              required
              value={form.fullName}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-slate-700">Phone Number</span>
            <input
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              onChange={(event) => setForm((current) => ({ ...current, phoneNumber: event.target.value }))}
              required
              value={form.phoneNumber}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-slate-700">Email</span>
            <input
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              type="email"
              value={form.email}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-slate-700">Alternative Phone</span>
            <input
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              onChange={(event) => setForm((current) => ({ ...current, altPhone: event.target.value }))}
              value={form.altPhone}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-slate-700">Role</span>
            <select
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))}
              value={form.role}
            >
              {formRoles.map((role) => (
                <option key={role} value={role}>
                  {formatEnumLabel(role)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-slate-700">Team / Group</span>
            <input
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              onChange={(event) => setForm((current) => ({ ...current, teamName: event.target.value }))}
              value={form.teamName}
            />
          </label>
          <label className="sm:col-span-2 flex flex-col gap-1.5">
            <span className="text-xs font-medium text-slate-700">Notes</span>
            <textarea
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
              rows={3}
              value={form.notes}
            />
          </label>
          <div className="sm:col-span-2 flex items-center gap-3">
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {isEditing ? "Save User" : "Create User"}
            </Button>
            {isEditing ? (
              <Button type="button" variant="secondary" onClick={resetForm}>
                Cancel Edit
              </Button>
            ) : null}
          </div>
        </form>
      </PageSection>

      <Drawer
        onClose={() => {
          setSelectedUserId(null)
          setIsEditing(false)
          updateRouteState({ selectedUserId: null })
        }}
        open={Boolean(selectedUserId)}
        title={detail?.fullName ?? "User Detail"}
      >
        {detail ? (
          <div className="flex flex-col gap-4">
            <PageSection title="Profile">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1 text-sm text-slate-600">
                  <p className="font-semibold text-slate-900">{detail.userId}</p>
                  <p>{detail.phoneNumber}</p>
                  <p>{detail.email ?? "-"}</p>
                  <p>{detail.altPhone ?? "-"}</p>
                </div>
                <div className="space-y-1 text-sm text-slate-600">
                  <StatusPill tone={statusTone(detail.accountStatus)}>{detail.accountStatus}</StatusPill>
                  <p>Role: {formatEnumLabel(detail.role)}</p>
                  <p>Team: {detail.teamName ?? "-"}</p>
                  <p>Created: {formatDateTime(detail.createdAt)}</p>
                  <p>Created by: {detail.createdByName ?? "-"}</p>
                  <p>Last seen: {formatDateTime(detail.lastSeenAt)}</p>
                </div>
              </div>
              {detail.notes ? <p className="mt-3 text-sm text-slate-600">{detail.notes}</p> : null}
            </PageSection>

            <PageSection title="Activity Summary">
              <div className="grid grid-cols-1 gap-2 text-sm text-slate-600 sm:grid-cols-2">
                <p>Last login: {formatDateTime(detail.activitySummary?.lastLoginAt)}</p>
                <p>Total logins: {detail.activitySummary?.totalLogins ?? 0}</p>
                <p>
                  Avg session duration: {detail.activitySummary?.averageSessionDurationMinutes ?? 0} min
                </p>
                <p>Failed attempts: {detail.activitySummary?.failedLoginAttempts ?? 0}</p>
              </div>
            </PageSection>

            {detail.assignedWork ? (
              <PageSection title="Assigned Work">
                <div className="grid grid-cols-1 gap-2 text-sm text-slate-600 sm:grid-cols-2">
                  <p>Assigned clients: {detail.assignedWork.assignedClientCount ?? 0}</p>
                  <p>Outstanding amount: {detail.assignedWork.outstandingAmount ?? 0}</p>
                  <p>Recovered amount: {detail.assignedWork.recoveredAmount ?? 0}</p>
                  <p>
                    Pipeline mix: {detail.assignedWork.newCount ?? 0} new / {detail.assignedWork.oldCount ?? 0} old / {detail.assignedWork.promiseToPayCount ?? 0} ptp
                  </p>
                </div>
              </PageSection>
            ) : null}

            {detail.verificationStats ? (
              <PageSection title="Verification Stats">
                <div className="grid grid-cols-1 gap-2 text-sm text-slate-600 sm:grid-cols-2">
                  <p>Reviews completed: {detail.verificationStats.reviewsCompleted ?? 0}</p>
                  <p>Approved: {detail.verificationStats.approvals ?? 0}</p>
                  <p>Rejected: {detail.verificationStats.rejections ?? 0}</p>
                  <p>Rejection rate: {detail.verificationStats.rejectionRate ?? 0}%</p>
                </div>
              </PageSection>
            ) : null}

            {detail.recentAuditEvents?.length ? (
              <PageSection title="Recent Audit Events">
                <ul className="m-0 flex list-none flex-col gap-3 p-0">
                  {detail.recentAuditEvents.map((event: any) => (
                    <li key={event.publicId} className="rounded-lg border border-slate-200 px-3 py-2">
                      <strong className="text-sm text-slate-800">{event.actionType}</strong>
                      <p className="mt-1 text-xs text-slate-500">{formatDateTime(event.timestamp)}</p>
                      {event.description ? (
                        <p className="mt-1 text-sm text-slate-600">{event.description}</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </PageSection>
            ) : null}

            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" onClick={startEdit}>
                Edit User
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => actionMutation.mutate({ action: "unlock", userId: detail.userId })}
              >
                Unlock
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => actionMutation.mutate({ action: "resetOtp", userId: detail.userId })}
              >
                Reset OTP
              </Button>
              <Button
                type="button"
                variant={detail.accountStatus === "DEACTIVATED" ? "outline" : "danger"}
                onClick={() =>
                  actionMutation.mutate({
                    action: detail.accountStatus === "DEACTIVATED" ? "reactivate" : "deactivate",
                    userId: detail.userId,
                  })
                }
              >
                {detail.accountStatus === "DEACTIVATED" ? "Reactivate" : "Deactivate"}
              </Button>
              {isSuperAdmin ? (
                <a
                  className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  href={`/audit-logs?search=${encodeURIComponent(detail.userId)}`}
                >
                  View Activity Log
                </a>
              ) : null}
            </div>
          </div>
        ) : null}
      </Drawer>

      <Modal
        open={isPermissionsOpen}
        onClose={() => setIsPermissionsOpen(false)}
        title="Role Permissions"
        description="Reference matrix for user management, KYC review, and privileged platform capabilities."
        widthClassName="max-w-4xl"
      >
        <div
          role="region"
          aria-label="Role permissions table"
          className="max-w-full overflow-hidden rounded-xl border border-slate-200"
        >
          <DataTable>
            <TableHead>
              <Th className="min-w-[160px]">Role</Th>
              <Th className="min-w-[140px]">Manage Users</Th>
              <Th className="min-w-[120px]">KYC Review</Th>
              <Th className="min-w-[120px]">Targets</Th>
              <Th className="min-w-[140px]">Disbursements</Th>
              <Th className="min-w-[120px]">Audit</Th>
              <Th className="min-w-[140px]">System Config</Th>
            </TableHead>
            <TableBody>
              {ROLE_PERMISSION_ROWS.map((row) => (
                <TableRow key={row.role}>
                  <Td className="min-w-[160px] font-semibold text-slate-800">
                    {formatEnumLabel(row.role)}
                  </Td>
                  <Td className="min-w-[140px]">{row.canManageUsers}</Td>
                  <Td className="min-w-[120px]">{row.canReviewKyc}</Td>
                  <Td className="min-w-[120px]">{row.canManageTargets}</Td>
                  <Td className="min-w-[140px]">{row.canManageDisbursements}</Td>
                  <Td className="min-w-[120px]">{row.canViewAudit}</Td>
                  <Td className="min-w-[140px]">{row.canManageConfig}</Td>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        </div>
      </Modal>
    </div>
  )
}
